import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../Store/hooks';
import { getCoachInfo } from '../Store/StudentSlices/getCoachSlice';
import { signOutAccount } from '../Store/StudentSlices/signOutAccountSlice';

type LinkedAccountState = {
  linkedEmail?: string;
};

const LinkedAccount = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const state = location.state as LinkedAccountState | null;
  const linkedEmail = state?.linkedEmail ?? '';
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!linkedEmail) {
      navigate('/login', { replace: true });
    }
  }, [linkedEmail, navigate]);

  const handleContinueToAccount = async () => {
    try {
      await dispatch(getCoachInfo()).unwrap();
    } catch (err) {
      // Error is handled by the slice
    }
    navigate('/', { replace: true });
  };

  const handleBackToLogin = async () => {
    try {
      await dispatch(signOutAccount()).unwrap();
    } catch (err) {
      // Ignore sign-out errors here
    }
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white px-4 py-7 flex flex-col">
      <div className="w-full max-w-6xl mx-auto flex-1 flex flex-col ">
        <div className="text-center mt-2">
          <h1 className="text-lg font-semibold text-gray-900">Onefinnet Coaching</h1>
          <p className="text-xs text-gray-500">Expert coaching, personalized growth</p>
        </div>

        <div className="mt-20 text-center">
          <div className="mx-auto max-w-3xl relative overflow-hidden rounded-lg">
            {!imageLoaded && (
              <div className="w-full h-[400px] bg-gray-100 animate-pulse rounded-lg flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            )}
            <img
              src="https://ecn.blob.core.windows.net/ecn/images/FINAL Dashboard1.png"
              alt="Dashboard preview"
              className={`w-full h-auto transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
              onLoad={() => setImageLoaded(true)}
              loading="eager"
            />
            {imageLoaded && (
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-white via-white/60 to-transparent"></div>
            )}
          </div>

          <div className={`relative space-y-6 px-4 transition-all duration-300 ${imageLoaded ? '-mt-32' : 'mt-6'}`}>
            <h2 className="text-[20px] font-semibold text-gray-900 leading-relaxed">
              We found a coach account linked to {linkedEmail}
            </h2>

            <div className="flex flex-col items-center gap-4">
              <button
                type="button"
                onClick={handleContinueToAccount}
                className="w-full max-w-lg py-3 bg-gray-900 text-white rounded-lg text-sm font-medium transition-all hover:bg-gray-800 active:scale-[0.98]"
              >
                Go to Coach Account
              </button>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-sm text-gray-900 underline hover:text-gray-700 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>

        {/* <div className="mb-4 text-center text-xs text-gray-500">
          If you need help,{' '}
          <button type="button" className="underline text-gray-800">
            Contact Support
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default LinkedAccount;
